/**
 * Community Page
 *
 * /community — Distribution hub guiding users into the Quanthop ecosystem.
 * Each platform has a clear role in the funnel:
 * TradingView (discovery) → X (conversation) → Discord (retention) → Email (ownership)
 */

import { type FC, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faEnvelope,
  faChartLine,
  faVideo,
  faArrowRight,
  faArrowUpRightFromSquare,
  faCheck,
  faSpinner,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons';
import PublicNavBar from '../components/layout/PublicNavBar';
import PublicFooter from '../components/layout/PublicFooter';
import { usePageMeta } from '../hooks/usePageMeta';

/* ── API config ── */
const API_BASE = import.meta.env.VITE_API_URL || '/api';

/* ── Scroll reveal hook ── */
function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  const style: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(18px)',
    transition: 'opacity 0.7s ease, transform 0.7s ease',
  };

  return { ref, style };
}

/* ── Platform cards data ── */
const platforms = [
  {
    id: 'discord',
    label: 'Discord',
    icon: faComments,
    role: 'Core community',
    bullets: [
      'Research discussions with other quant traders',
      'Backtesting and optimisation talk',
      'Early access to new features',
    ],
    cta: 'Join Discord',
    href: 'https://discord.gg/9SMxNEMRYu',
    primary: true,
  },
  {
    id: 'tradingview',
    label: 'TradingView',
    icon: faChartLine,
    role: 'Discovery',
    bullets: [
      'Free TDR indicator for your charts',
      'Strategy ideas and visual research',
      'Entry point for new users',
    ],
    cta: 'View on TradingView',
    href: 'https://www.tradingview.com/u/Quanthop/',
  },
  {
    id: 'x',
    label: 'X (Twitter)',
    icon: faXTwitter,
    role: 'Conversation',
    bullets: [
      'Daily insights and strategy thoughts',
      'Public discussions and research threads',
      'Platform updates and announcements',
    ],
    cta: 'Follow on X',
    href: 'https://x.com/quanthop',
  },
  {
    id: 'youtube',
    label: 'YouTube',
    icon: faVideo,
    role: 'Deep dives',
    bullets: [
      'Strategy walkthroughs and breakdowns',
      'Platform tutorials',
      'Research process explained',
    ],
    cta: 'Subscribe',
    href: 'https://www.youtube.com/@Quanthop',
  },
];

/* ── Getting started steps ── */
const steps = [
  {
    num: 1,
    text: 'Get the free TDR indicator on TradingView',
    link: '/labs/tradingview-indicator-tdr',
    internal: true,
  },
  {
    num: 2,
    text: 'Join the email list for access updates',
    link: '#email-signup',
    internal: true,
  },
  {
    num: 3,
    text: 'Join Discord for research and community',
    link: 'https://discord.gg/9SMxNEMRYu',
    internal: false,
  },
];

/* ── Form states ── */
type FormState = 'idle' | 'submitting' | 'success' | 'error';

const CommunityPage: FC = () => {
  usePageMeta({ title: 'Community', description: 'Join the Quanthop ecosystem. Discord, TradingView, X, and YouTube. Research, build, and validate with other systematic traders.' });
  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const revHero = useScrollReveal(0.1);
  const revPlatforms = useScrollReveal(0.1);
  const revSteps = useScrollReveal(0.15);
  const revEmail = useScrollReveal(0.15);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!email.trim()) {
      setErrorMessage('Please enter your email.');
      return;
    }

    setFormState('submitting');

    try {
      const response = await fetch(`${API_BASE}/labs/community-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Submission failed');
      }

      setFormState('success');
    } catch {
      setFormState('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30 flex flex-col">
      <PublicNavBar />

      {/* ── Hero ── */}
      <section
        className="relative pt-32 pb-24 px-4 sm:px-6 overflow-hidden"
        ref={revHero.ref as React.RefObject<HTMLElement>}
        style={revHero.style}
      >
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-16 left-1/2 -translate-x-1/2 w-[600px] h-[350px] rounded-full bg-[#9d4edd]/[0.035] blur-[160px]" />
        </div>

        <div className="max-w-[900px] mx-auto text-center relative z-10">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 mb-6">
            Community
          </div>
          <h1 className="text-3xl md:text-[42px] font-bold text-white tracking-[-0.02em] leading-[1.15] mb-5">
            Join the Quanthop ecosystem
          </h1>
          <p className="text-[15px] text-gray-300/85 leading-[1.7] max-w-[560px] mx-auto mb-3">
            Research. Build. Validate. With other serious traders.
          </p>
          <p className="text-[13px] text-gray-500/70">
            Each platform serves a purpose in your trading research workflow.
          </p>
        </div>
      </section>

      {/* ── Platform Cards ── */}
      <section
        className="py-24 px-4 sm:px-6 border-t border-gray-700/40"
        ref={revPlatforms.ref as React.RefObject<HTMLElement>}
        style={revPlatforms.style}
      >
        <div className="max-w-[1100px] mx-auto">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 text-center mb-4">
            Where to find us
          </div>
          <h2 className="text-[22px] font-semibold text-white text-center tracking-[-0.02em] mb-12">
            Choose your entry point
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {platforms.map((p) => (
              <a
                key={p.id}
                href={p.comingSoon ? undefined : p.href}
                target={p.comingSoon ? undefined : '_blank'}
                rel={p.comingSoon ? undefined : 'noopener noreferrer'}
                className={`group relative rounded-lg border px-5 py-5 transition-all duration-300 ${p.primary
                  ? 'border-[#9d4edd]/25 hover:border-[#9d4edd]/50'
                  : 'border-white/[0.06] hover:border-white/[0.12]'
                  } ${p.comingSoon ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                style={{ background: p.primary ? 'rgba(157, 78, 221, 0.03)' : 'rgba(157, 78, 221, 0.01)' }}
              >
                {p.primary && (
                  <div className="absolute top-4 right-4 text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/60">
                    Recommended
                  </div>
                )}
                {p.comingSoon && (
                  <div className="absolute top-4 right-4 text-[10px] font-mono tracking-[0.15em] uppercase text-gray-600">
                    Coming soon
                  </div>
                )}

                <div>
                  <div className="w-9 h-9 rounded-md bg-[#9d4edd]/[0.08] flex items-center justify-center mb-4">
                    <FontAwesomeIcon icon={p.icon} className="text-[14px] text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2.5 mb-0.5">
                      <h3 className="text-[15px] font-semibold text-white">{p.label}</h3>
                    </div>
                    <span className="text-[10px] font-mono tracking-[0.1em] uppercase text-gray-500/60">{p.role}</span>
                    <ul className="space-y-1 mt-2.5">
                      {p.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-gray-400/85">
                          <span className="w-1 h-1 rounded-full bg-[#9d4edd]/30 mt-[7px] flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    {!p.comingSoon && (
                      <div className="mt-4 flex items-center gap-1.5 text-[13px] font-medium text-[#9d4edd] group-hover:text-[#8b3ec7] transition-colors">
                        {p.cta}
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
                      </div>
                    )}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Getting Started ── */}
      <section
        className="py-24 px-4 sm:px-6 border-t border-gray-700/40"
        ref={revSteps.ref as React.RefObject<HTMLElement>}
        style={revSteps.style}
      >
        <div className="max-w-[600px] mx-auto">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 text-center mb-4">
            Quick start
          </div>
          <h2 className="text-[22px] font-semibold text-white text-center tracking-[-0.02em] mb-3">
            Getting started
          </h2>
          <p className="text-[14px] text-gray-400/80 text-center mb-10">
            Not sure where to begin? Three steps.
          </p>

          <div className="space-y-0">
            {steps.map((s, idx) => {
              const content = (
                <div className="group flex items-center gap-4 rounded-lg border border-white/[0.06] px-5 py-4 hover:border-[#9d4edd]/25 transition-all"
                  style={{ background: 'rgba(157, 78, 221, 0.015)' }}
                >
                  <div className="w-8 h-8 rounded-full bg-[#9d4edd]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[12px] font-mono font-bold text-[#9d4edd]/70">{s.num}</span>
                  </div>
                  <span className="text-[14px] text-gray-300/85 flex-1">{s.text}</span>
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="text-[11px] text-gray-600 group-hover:text-[#9d4edd]/60 transition-colors"
                  />
                </div>
              );

              const connector = idx < steps.length - 1 && (
                <div className="flex justify-center py-1">
                  <div className="w-px h-6 bg-gradient-to-b from-[#9d4edd]/20 to-[#9d4edd]/05" />
                </div>
              );

              if (s.internal) {
                return (
                  <div key={s.num}>
                    {s.link.startsWith('#') ? (
                      <a href={s.link} className="block">{content}</a>
                    ) : (
                      <Link to={s.link} className="block">{content}</Link>
                    )}
                    {connector}
                  </div>
                );
              }

              return (
                <div key={s.num}>
                  <a href={s.link} target="_blank" rel="noopener noreferrer" className="block">
                    {content}
                  </a>
                  {connector}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Email Signup ── */}
      <section
        id="email-signup"
        className="py-24 px-4 sm:px-6 border-t border-gray-700/40"
        ref={revEmail.ref as React.RefObject<HTMLElement>}
        style={revEmail.style}
      >
        <div className="max-w-[520px] mx-auto text-center">
          <div className="w-9 h-9 rounded-md bg-[#9d4edd]/[0.08] flex items-center justify-center mx-auto mb-5">
            <FontAwesomeIcon icon={faEnvelope} className="text-[14px] text-[#9d4edd]/70" />
          </div>
          <h2 className="text-[22px] font-semibold text-white tracking-[-0.02em] mb-3">Stay in the loop</h2>
          <p className="text-[14px] text-gray-400/80 mb-8">
            Early access, platform invites, and occasional research notes. No spam.
          </p>

          {formState === 'success' ? (
            <div className="flex items-center justify-center gap-2 text-emerald-400 bg-emerald-400/[0.06] border border-emerald-400/20 rounded-lg px-6 py-4 text-[14px]">
              <FontAwesomeIcon icon={faCheck} />
              <span>You're on the list. Check your inbox.</span>
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 px-4 py-2.5 rounded-lg bg-gray-900/50 border border-gray-800 text-white text-[14px] placeholder-gray-600 focus:outline-none focus:border-[#9d4edd]/40 transition-colors"
                disabled={formState === 'submitting'}
              />
              <button
                type="submit"
                disabled={formState === 'submitting'}
                className="px-6 py-2.5 rounded-lg bg-[#9d4edd] hover:bg-[#8b3ec7] text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {formState === 'submitting' ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  <>
                    Join the list
                    <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                  </>
                )}
              </button>
            </form>
          )}

          {formState === 'error' && errorMessage && (
            <div className="mt-3 flex items-center justify-center gap-2 text-amber-400 text-[13px]">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-[11px]" />
              {errorMessage}
            </div>
          )}

          <p className="text-[11px] font-mono text-gray-600 mt-4">
            Unsubscribe anytime. No credit card required.
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <div className="mt-auto">
        <PublicFooter />
      </div>
    </div>
  );
};

export default CommunityPage;
